import API_BASE_URL from '../config';
import { getToken } from './authService';
import { getCategories } from './categoryService';

let campaignPromise = null;
let cachedCategoryTree = [];

export function setCachedCategoryTree(tree) {
  if (Array.isArray(tree) && tree.length) {
    cachedCategoryTree = tree;
  }
}

export function getCachedCategoryTree() {
  return cachedCategoryTree;
}

export async function getTradeInCampaign(forceRefresh = false) {
  if (forceRefresh) campaignPromise = null;

  // Preload and cache category tree in parallel if not yet cached
  if (!cachedCategoryTree.length) {
    getCategories().then(cats => {
      if (Array.isArray(cats) && cats.length) {
        cachedCategoryTree = cats;
      }
    }).catch(() => { });
  }

  if (campaignPromise) return campaignPromise;

  const url = forceRefresh
    ? `${API_BASE_URL}/campaigns/trade-in?_t=${Date.now()}`
    : `${API_BASE_URL}/campaigns/trade-in`;

  campaignPromise = fetch(url)
    .then(async (res) => {
      if (!res.ok) throw new Error('Failed to fetch campaign');
      return await res.json();
    })
    .catch((err) => {
      campaignPromise = null;
      console.error('Error fetching trade-in campaign:', err);
      return {
        enabled: true,
        name: "Sill të vjetrën, merr të renë",
        badgeText: "Fushata: ulje",
        noteTextDesktop: "Sill të vjetrën, merr të renë: ulja rritet nga {base}% në {full}%. Pajisja dorëzohet gjatë marrjes ose dërgesës së produktit.",
        noteTextMobile: "Ulja rritet nga {base}% në {full}%. Pajisja dorëzohet gjatë marrjes ose dërgesës.",
        showExcludedNote: true,
        excludedNoteText: "Nuk përfshihet në fushatën “Sill të vjetrën merr të renë”",
        checkboxLabel: "Kam pajisje të vjetër për ta dorëzuar",
        paymentNotice: "Keni deklaruar se do të dorëzoni pajisjen e vjetër. Nëse nuk e dorëzoni, çmimi kthehet në uljen bazë.",
        defaultRates: { baseDiscount: 15, fullDiscount: 30 },
        categoryRates: {},
        productOverrides: {},
        excludedSkus: ["B2TVNL55LG4K55NANO81A6A"]
      };
    });

  return campaignPromise;
}

export async function updateTradeInCampaign(data) {
  const token = getToken();
  const res = await fetch(`${API_BASE_URL}/campaigns/trade-in`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('Failed to save campaign');
  campaignPromise = null; // Invalidate cache
  const result = await res.json();

  if (typeof window !== 'undefined') {
    const updated = result.campaign || data;
    window.dispatchEvent(new CustomEvent('tradeInCampaignUpdated', { detail: { campaign: updated } }));
  }

  return result;
}

/**
 * Resolves category ID string from category ref
 */
export function getCategoryIdStr(catRef) {
  if (catRef === undefined || catRef === null) return '';
  if (typeof catRef === 'object') {
    return String(catRef.id || catRef._id || catRef.categoryId || catRef.category_id || catRef.name || '');
  }
  return String(catRef).trim();
}

export function getCategoryPath(catRef, categoryTree = []) {
  const targetId = getCategoryIdStr(catRef);
  const tree = (Array.isArray(categoryTree) && categoryTree.length) ? categoryTree : cachedCategoryTree;
  if (!targetId || !Array.isArray(tree) || !tree.length) return [];

  function find(list, id, path = []) {
    for (const n of list) {
      if (!n) continue;
      const currentPath = [...path, n];
      const nodeId = getCategoryIdStr(n.id || n);
      if (nodeId === id || String(n.name || '').trim().toLowerCase() === id.toLowerCase()) {
        return currentPath;
      }
      if (n.subcategories && n.subcategories.length) {
        const found = find(n.subcategories, id, currentPath);
        if (found) return found;
      }
    }
    return null;
  }

  return find(tree, targetId) || [];
}

/**
 * Finds the top-level parent category ID from category tree
 */
export function getTopParentCategoryId(catRef, categoryTree = []) {
  const tree = (Array.isArray(categoryTree) && categoryTree.length) ? categoryTree : cachedCategoryTree;
  const path = getCategoryPath(catRef, tree);
  if (path && path.length) {
    return getCategoryIdStr(path[0].id || path[0]);
  }
  const targetId = getCategoryIdStr(catRef);
  return targetId;
}

/**
 * Evaluates trade-in campaign discount rates and price calculations for a cart/product item
 */
export function evaluateItemCampaign({
  item,
  campaign,
  product = null,
  categoryTree = []
}) {
  const p = product || item;
  const origPrice = Number(p?.price || item?.price || 0);
  const salePrice = p?.salePrice !== undefined && p?.salePrice !== null ? Number(p.salePrice) : null;
  const offerPrice = p?.offerPrice !== undefined && p?.offerPrice !== null ? Number(p.offerPrice) : null;

  let effectiveBasePrice = origPrice;
  if (offerPrice && offerPrice > 0 && offerPrice < origPrice) {
    effectiveBasePrice = offerPrice;
  } else if (salePrice && salePrice > 0 && salePrice < origPrice) {
    effectiveBasePrice = salePrice;
  }

  const servicesSum = Array.isArray(item?.services)
    ? item.services.reduce((ss, sv) => ss + Number((sv && sv.price) || 0), 0)
    : 0;

  const qty = Math.max(1, Number(item?.qty || item?.quantity || 1));
  const fallbackSubtotal = (effectiveBasePrice + servicesSum) * qty;

  const fallbackResult = {
    isEligible: false,
    isExcluded: false,
    baseDiscount: 0,
    fullDiscount: 0,
    baseDiscountPct: 0,
    tradeInTotalDiscountPct: 0,
    unitInitialPrice: origPrice,
    unitBaseDiscountedPrice: effectiveBasePrice,
    unitFullDiscountedPrice: effectiveBasePrice,
    unitBasePriceWithServices: effectiveBasePrice + servicesSum,
    unitFullPriceWithServices: effectiveBasePrice + servicesSum,
    unitPriceWithTradeIn: effectiveBasePrice + servicesSum,
    baseSubtotal: fallbackSubtotal,
    fullSubtotal: fallbackSubtotal,
    effectiveSubtotalWithTradeIn: fallbackSubtotal,
    normalSubtotal: fallbackSubtotal,
    unitSavings: 0,
    totalTradeInSavings: 0,
    qty,
    servicesSum,
    showExcludedNote: Boolean(campaign?.showExcludedNote !== false),
    excludedNoteText: campaign?.excludedNoteText || 'Nuk përfshihet në fushatën “Sill të vjetrën merr të renë”'
  };

  // If trade-in campaign is not provided or disabled, return fallback (not eligible, not excluded)
  if (!campaign || !campaign.enabled) return fallbackResult;

  const sku = String(p?.sku || p?.kodi_i_produktit || item?.sku || item?.kodi_i_produktit || '').trim();
  const prodId = String(p?.id || item?.id || '').trim();

  // Check explicit SKU exclusion
  const excludedSkus = Array.isArray(campaign.excludedSkus) ? campaign.excludedSkus : [];
  const isExcluded = excludedSkus.some(ex => {
    const clean = String(ex || '').trim().toLowerCase();
    return clean !== '' && (clean === sku.toLowerCase() || (prodId && clean === prodId.toLowerCase()));
  });

  if (isExcluded) {
    return {
      ...fallbackResult,
      isExcluded: true,
      isEligible: false
    };
  }

  // Resolve Rates: Product Overrides -> Category Rates -> Default Rates
  let baseDiscount = 15;
  let fullDiscount = 30;

  const overrides = campaign.productOverrides || {};
  let directOverride = overrides[sku] || overrides[prodId];
  if (!directOverride && sku) {
    const skuLower = sku.toLowerCase();
    const foundOverrideKey = Object.keys(overrides).find(k => k.trim().toLowerCase() === skuLower);
    if (foundOverrideKey) directOverride = overrides[foundOverrideKey];
  }

  if (directOverride) {
    baseDiscount = Number(directOverride.baseDiscount ?? directOverride.tier1 ?? 15);
    fullDiscount = Number(directOverride.fullDiscount ?? directOverride.tier2 ?? 30);
  } else {
    const catRates = campaign.categoryRates || {};
    const tree = (Array.isArray(categoryTree) && categoryTree.length) ? categoryTree : cachedCategoryTree;

    // Collect all candidate category references
    const candidateCatRefs = [];
    if (p?.categoryId !== undefined && p?.categoryId !== null && p.categoryId !== '') candidateCatRefs.push(p.categoryId);
    if (p?.category_id !== undefined && p?.category_id !== null && p.category_id !== '') candidateCatRefs.push(p.category_id);
    if (p?.category) candidateCatRefs.push(p.category);
    if (Array.isArray(p?.categoryIds)) candidateCatRefs.push(...p.categoryIds);
    if (Array.isArray(p?.categories)) candidateCatRefs.push(...p.categories);
    if (item?.categoryId !== undefined && item?.categoryId !== null && item.categoryId !== '') candidateCatRefs.push(item.categoryId);
    if (item?.category_id !== undefined && item?.category_id !== null && item.category_id !== '') candidateCatRefs.push(item.category_id);
    if (item?.category) candidateCatRefs.push(item.category);
    if (Array.isArray(item?.categoryIds)) candidateCatRefs.push(...item.categoryIds);
    if (Array.isArray(item?.categories)) candidateCatRefs.push(...item.categories);

    let foundCatRate = null;

    const getRateByKey = (key) => {
      if (key === undefined || key === null || key === '') return null;
      const kStr = String(key).trim();
      if (catRates[kStr] !== undefined) return catRates[kStr];
      if (!isNaN(Number(kStr)) && catRates[Number(kStr)] !== undefined) return catRates[Number(kStr)];
      return null;
    };

    // 1. Direct match on candidates
    for (const ref of candidateCatRefs) {
      const idStr = getCategoryIdStr(ref);
      const direct = getRateByKey(idStr);
      if (direct) {
        foundCatRate = direct;
        break;
      }
    }

    // 2. Hierarchy inheritance in category tree (leaf -> intermediate ancestors -> root)
    if (!foundCatRate && Array.isArray(tree) && tree.length) {
      for (const ref of candidateCatRefs) {
        const path = getCategoryPath(ref, tree);
        if (Array.isArray(path) && path.length) {
          for (let i = path.length - 1; i >= 0; i--) {
            const stepId = getCategoryIdStr(path[i].id || path[i]);
            const stepRate = getRateByKey(stepId);
            if (stepRate) {
              foundCatRate = stepRate;
              break;
            }
          }
        }
        if (foundCatRate) break;

        const topCatId = getTopParentCategoryId(ref, tree);
        const topRate = getRateByKey(topCatId);
        if (topRate) {
          foundCatRate = topRate;
          break;
        }
      }
    }

    if (foundCatRate) {
      baseDiscount = Number(foundCatRate.baseDiscount ?? foundCatRate.tier1 ?? 15);
      fullDiscount = Number(foundCatRate.fullDiscount ?? foundCatRate.tier2 ?? 30);
    } else {
      const def = campaign.defaultRates || { baseDiscount: 15, fullDiscount: 30 };
      baseDiscount = Number(def.baseDiscount ?? def.tier1 ?? 15);
      fullDiscount = Number(def.fullDiscount ?? def.tier2 ?? 30);
    }
  }

  // Sanitize rates
  baseDiscount = Math.max(0, Math.min(100, isNaN(baseDiscount) ? 15 : baseDiscount));
  fullDiscount = Math.max(0, Math.min(100, isNaN(fullDiscount) ? 30 : fullDiscount));

  if (baseDiscount === 0 && fullDiscount === 0) {
    return {
      ...fallbackResult,
      isExcluded: true,
      isEligible: false
    };
  }

  // Calculations based on initial price as per requirements
  const unitBaseDiscountedPrice = origPrice > 0
    ? Math.round(origPrice * (1 - baseDiscount / 100))
    : 0;
  const unitFullDiscountedPrice = origPrice > 0
    ? Math.round(origPrice * (1 - fullDiscount / 100))
    : 0;

  const unitBasePriceWithServices = unitBaseDiscountedPrice + servicesSum;
  const unitFullPriceWithServices = unitFullDiscountedPrice + servicesSum;

  const baseSubtotal = unitBasePriceWithServices * qty;
  const fullSubtotal = unitFullPriceWithServices * qty;

  const unitSavings = Math.max(0, unitBaseDiscountedPrice - unitFullDiscountedPrice);
  const totalTradeInSavings = unitSavings * qty;

  return {
    isEligible: true,
    isExcluded: false,
    baseDiscount,
    fullDiscount,
    baseDiscountPct: baseDiscount,
    tradeInTotalDiscountPct: fullDiscount,
    unitInitialPrice: origPrice,
    unitBaseDiscountedPrice,
    unitFullDiscountedPrice,
    unitBasePriceWithServices,
    unitFullPriceWithServices,
    unitPriceWithTradeIn: unitFullPriceWithServices,
    unit1PriceWithTradeIn: unitFullPriceWithServices,
    baseSubtotal,
    fullSubtotal,
    effectiveSubtotalWithTradeIn: fullSubtotal,
    normalSubtotal: baseSubtotal,
    unitSavings,
    totalTradeInSavings,
    qty,
    servicesSum,
    showExcludedNote: Boolean(campaign.showExcludedNote !== false),
    excludedNoteText: campaign.excludedNoteText || 'Nuk përfshihet në fushatën “Sill të vjetrën merr të renë”'
  };
}
